import { MBO_CART_ORIGIN, MBO_DESTINATION_OVERRIDES, MBO_SIGNIN_RETURN_PATH } from 'app/shared/consts/mbo-destinations';

/**
 * Mindbody stamps a visitor id on a first-party cookie in the embedding page and forwards it
 * on the destination URL — that is how the widget's visitor is carried into the cart/account
 * pages. Opening `data-url` without it lands the user as an unrecognised visitor.
 */
const MBO_VISITOR_COOKIE = 'mbvid';

/**
 * Marketing params Mindbody forwards from the embedding page so their attribution chain
 * survives the hop. Mirrors the list in Mindbody's own widget script.
 */
const FORWARDED_PARAMS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_term',
  'utm_content',
  'utm_id',
  'gclid',
  'gbraid',
  'wbraid',
  'fbclid',
  '_ga',
  '_gl',
];

const readVisitorId = (cookie: string): string | null => {
  const parts = `; ${cookie}`.split(`; ${MBO_VISITOR_COOKIE}=`);

  return parts.length > 1 ? (parts.pop()?.split(';')[0] ?? null) : null;
};

const readOrigin = (href: string): string | null => {
  try {
    return new URL(href).origin;
  } catch {
    return null;
  }
};

/** `/sites/16805/cart/add_booking` → `cart/add_booking`; unrecognised shapes key as the raw path. */
export const getDestinationKey = (url: URL): string => url.pathname.replace(/^\/sites\/\d+\//, '');

const applyOverride = (url: URL): URL => {
  const override = MBO_DESTINATION_OVERRIDES[getDestinationKey(url)];

  if (!override) {
    return url;
  }

  // The override supplies the destination; Mindbody's own query string (booking item, widget
  // type, site) still describes *what* the user picked, so it is carried across.
  const overridden = new URL(override, url.origin);

  url.searchParams.forEach((value, key) => {
    if (!overridden.searchParams.has(key)) {
      overridden.searchParams.append(key, value);
    }
  });

  return overridden;
};

/**
 * Builds the URL to send the user to for a Mindbody widget action, reproducing the params
 * Mindbody's own handler adds (visitor id, marketing attribution, embedding origin) and
 * applying any destination override.
 *
 * `embedded_url` is load-bearing: the cart stores it against the session on the first request
 * and renders it back as the `postMessage` target origin when it needs the embedder to move
 * the user (see `useMboWidgetModal`). Omit it and the cart emits `new URL('')`, which throws —
 * send the wrong origin and the message is silently discarded. Either way the cart appears to
 * hang, which is exactly what it did before we sent this.
 *
 * Returns `null` when the destination can't be parsed or doesn't resolve to `MBO_CART_ORIGIN` —
 * the caller must then leave the click alone so Mindbody's own handling takes over. This is the
 * only thing standing between a widget-supplied `data-url` and an arbitrary origin loading in the
 * dialog's frame, so it is a security boundary, not just input validation.
 *
 * @param dataUrl the trigger's `data-url`
 * @param href    the embedding page's own URL — its ORIGIN becomes `embedded_url`
 * @param search  the embedding page's query string, for attribution passthrough
 * @param cookie  the embedding page's cookies, for the visitor id
 */
export const buildMboLaunchUrl = (dataUrl: string, href: string, search: string, cookie: string): string | null => {
  let url: URL;

  try {
    url = new URL(dataUrl);
  } catch {
    return null;
  }

  url = applyOverride(url);

  if (url.origin !== MBO_CART_ORIGIN) {
    return null;
  }

  const visitorId = readVisitorId(cookie);

  if (visitorId && !url.searchParams.has(MBO_VISITOR_COOKIE)) {
    url.searchParams.append(MBO_VISITOR_COOKIE, visitorId);
  }

  // Our origin plus a fixed return path — never the caller's own href. The cart uses this two
  // ways: as the `postMessage` targetOrigin (path and query ignored) and as the redirect target
  // for the sign-in window once the user authenticates. A bare origin satisfies the first and
  // breaks the second: the window lands on the app root with no launch params and boots into
  // the error page. The fixed path is what `index.tsx` matches to close that window instead,
  // and it still tells Mindbody nothing about which page the user came from.
  const embeddedOrigin = readOrigin(href);

  if (embeddedOrigin && !url.searchParams.has('embedded_url')) {
    url.searchParams.append('embedded_url', `${embeddedOrigin}${MBO_SIGNIN_RETURN_PATH}`);
  }

  const pageParams = new URLSearchParams(search);

  FORWARDED_PARAMS.forEach((param) => {
    const value = pageParams.get(param);

    if (value !== null && !url.searchParams.has(param)) {
      url.searchParams.append(param, value);
    }
  });

  return url.toString();
};
